<!-- app-subdomain-creators/src/layouts/PageThemeEditorLayout.vue -->
<template>
  <div class="min-h-screen flex flex-col bg-zinc-50">
    <div class="w-full h-16 bg-zinc-700 px-3 py-3 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <img class="h-10 w-auto" src="@/assets/media/images/platformalogostack.png" alt="Platforma Logo">
        <router-link
          to="/creator/page-editor"
          class="theme-tooltip flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-600 text-white hover:bg-zinc-800 transition"
          aria-label="Back to Page List"
          data-tooltip="Back to Page List"
        >
          <ArrowUturnLeftIcon class="w-5 h-5" />
        </router-link>
      </div>
      <div class="flex items-center gap-1 rounded-lg bg-zinc-800/80 p-1">
        <button
          v-for="option in editorViewportOptions"
          :key="option.key"
          type="button"
          class="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-medium transition"
          :class="editorViewportMode === option.key ? 'bg-[#2D8CFF] text-white' : 'bg-zinc-700 text-zinc-100 hover:bg-zinc-600'"
          :aria-label="`Switch template previews to ${option.label} view`"
          :aria-pressed="editorViewportMode === option.key"
          @click="setEditorViewportMode(option.key)"
        >
          <component :is="option.icon" class="h-4 w-4" />
          <span class="hidden xl:inline">{{ option.label }}</span>
        </button>
      </div>
    </div>

    <main class="theme-library-scroll flex-1 overflow-y-auto p-6 sm:p-8">
      <div class="mx-auto w-full max-w-[1600px]">
        <div class="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p class="text-xs font-extrabold uppercase tracking-[0.18em] text-pink-500">Page templates</p>
            <h1 class="mt-2 text-3xl font-black tracking-tight text-zinc-950">Start with a page that is ready to edit</h1>
            <p class="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
              Every template below is built from real sections, rows, columns, and elements. Preview it here, then customize the same content in the Page Editor.
            </p>
          </div>
          <label class="relative block w-full lg:w-[360px]">
            <span class="sr-only">Search templates</span>
            <MagnifyingGlassIcon class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search templates"
              class="w-full rounded-xl border border-zinc-300 bg-white py-2.5 pl-10 pr-4 text-sm text-zinc-700 shadow-sm placeholder-zinc-400 transition focus:border-[#2D8CFF] focus:outline-none focus:ring-1 focus:ring-[#2D8CFF]"
            >
          </label>
        </div>

        <div class="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="template in filteredTemplates"
            :key="template.id"
            class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            <button
              type="button"
              class="group relative block h-[252px] w-full overflow-hidden bg-zinc-100 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2D8CFF]"
              :aria-label="`Preview ${template.name}`"
              @click="openPreview(template)"
            >
              <div v-if="template.id === 'blank'" class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-200">
                <div class="rounded-xl border border-dashed border-zinc-300 bg-white/80 px-8 py-6 text-center shadow-sm">
                  <PlusIcon class="mx-auto h-7 w-7 text-pink-500" />
                  <p class="mt-2 text-sm font-bold text-zinc-700">Blank Canvas</p>
                  <p class="mt-1 text-xs text-zinc-500">One clean, editable section</p>
                </div>
              </div>
              <div v-else class="template-card-canvas pointer-events-none select-none">
                <PageRenderer :doc="template.doc" :selected-index="-1" viewport-mode="desktop" />
              </div>
              <div class="absolute inset-0 flex items-center justify-center bg-zinc-950/0 transition group-hover:bg-zinc-950/20">
                <span class="translate-y-2 rounded-xl bg-white px-4 py-2 text-sm font-extrabold text-zinc-900 opacity-0 shadow-lg transition group-hover:translate-y-0 group-hover:opacity-100">Open preview</span>
              </div>
            </button>

            <div class="p-5">
              <span class="inline-flex rounded-full bg-pink-50 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-pink-600">{{ template.category }}</span>
              <h2 class="mt-3 text-lg font-extrabold text-zinc-950">{{ template.name }}</h2>
              <p class="mt-1 min-h-[60px] text-sm leading-5 text-zinc-500">{{ template.description }}</p>
              <div v-if="template.id !== 'blank'" class="mt-3 flex flex-wrap gap-2 text-[11px] font-bold text-zinc-600">
                <span class="rounded-full bg-zinc-100 px-2.5 py-1">{{ template.stats.sections }} sections</span>
                <span class="rounded-full bg-zinc-100 px-2.5 py-1">{{ template.stats.elements }} editable elements</span>
                <span class="rounded-full bg-pink-50 px-2.5 py-1 text-pink-600">{{ template.stats.images }} full images</span>
              </div>
              <div class="mt-5 grid grid-cols-2 gap-3">
                <button type="button" class="rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50" @click="openPreview(template)">Preview</button>
                <button
                  type="button"
                  class="rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#1b6ad1] disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="applying"
                  @click="selectTemplate(template)"
                >
                  {{ applyingTemplateId === template.id ? 'Applying…' : 'Use template' }}
                </button>
              </div>
            </div>
          </article>
        </div>

        <div v-if="filteredTemplates.length === 0" class="mt-8 rounded-2xl border border-dashed border-zinc-300 bg-white px-6 py-14 text-center text-sm text-zinc-500">
          No templates match “{{ searchQuery }}”.
        </div>
      </div>
    </main>

    <TransitionRoot as="template" :show="Boolean(previewTemplate)">
      <Dialog class="relative z-[80]" @close="closePreview">
        <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm" />
        </TransitionChild>
        <div class="fixed inset-0 overflow-y-auto p-3 sm:p-6">
          <div class="flex min-h-full items-center justify-center">
            <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 translate-y-3 scale-[0.98]" enter-to="opacity-100 translate-y-0 scale-100" leave="ease-in duration-150" leave-from="opacity-100 translate-y-0 scale-100" leave-to="opacity-0 translate-y-3 scale-[0.98]">
              <DialogPanel class="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-zinc-100 shadow-2xl">
                <header class="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-white px-5 py-4">
                  <div>
                    <DialogTitle class="text-lg font-extrabold text-zinc-950">{{ previewTemplate?.name }}</DialogTitle>
                    <p class="text-xs text-zinc-500">Live template preview · {{ editorViewportLabel }}</p>
                  </div>
                  <div class="flex items-center gap-3">
                    <div class="flex items-center gap-1 rounded-lg bg-zinc-100 p-1">
                      <button
                        v-for="option in editorViewportOptions"
                        :key="`preview-${option.key}`"
                        type="button"
                        class="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-bold transition"
                        :class="editorViewportMode === option.key ? 'bg-white text-[#2D8CFF] shadow-sm' : 'text-zinc-500 hover:text-zinc-800'"
                        :aria-label="`Preview in ${option.label} view`"
                        @click="setEditorViewportMode(option.key)"
                      >
                        <component :is="option.icon" class="h-4 w-4" />
                        <span class="hidden sm:inline">{{ option.label }}</span>
                      </button>
                    </div>
                    <button type="button" class="rounded-lg p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900" aria-label="Close template preview" @click="closePreview">
                      <XMarkIcon class="h-5 w-5" />
                    </button>
                  </div>
                </header>

                <div class="theme-library-scroll flex-1 overflow-auto p-4 sm:p-7">
                  <div class="mx-auto overflow-hidden bg-white shadow-xl transition-all duration-200" :class="previewViewportClasses">
                    <PageRenderer v-if="previewTemplate" class="pointer-events-none select-none" :doc="previewTemplate.doc" :selected-index="-1" :viewport-mode="editorViewportMode" />
                  </div>
                </div>

                <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 bg-white px-5 py-4">
                  <div>
                    <p class="text-xs leading-5 text-zinc-500">This exact structured content opens in the editor and is used by the published member page.</p>
                    <p v-if="previewTemplate?.id !== 'blank'" class="mt-1 text-[11px] font-bold text-pink-600">{{ previewTemplate?.stats?.sections }} sections · {{ previewTemplate?.stats?.elements }} editable elements · {{ previewTemplate?.stats?.images }} full images</p>
                  </div>
                  <button type="button" class="rounded-xl bg-[#2D8CFF] px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-[#1b6ad1] disabled:cursor-not-allowed disabled:opacity-60" :disabled="applying" @click="selectTemplate(previewTemplate)">
                    {{ applying ? 'Applying template…' : 'Use this template' }}
                  </button>
                </footer>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ArrowUturnLeftIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, DeviceTabletIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import PageRenderer from '@/Components/PageRenderer.vue'
import { buildPageTemplate, pageTemplates } from '@/utils/pageTemplates'

const route = useRoute()
const router = useRouter()
const EDITOR_VIEWPORT_STORAGE_KEY = 'platforma-site-editor-viewport-mode'
const editorViewportMode = ref('desktop')
const searchQuery = ref('')
const previewTemplate = ref(null)
const applying = ref(false)
const applyingTemplateId = ref('')
const editorViewportOptions = [
  { key: 'desktop', label: 'Desktop', icon: ComputerDesktopIcon },
  { key: 'tablet', label: 'Tablet', icon: DeviceTabletIcon },
  { key: 'mobile', label: 'Mobile', icon: DevicePhoneMobileIcon },
]

const templatesWithDocs = pageTemplates.map(template => ({ ...template, doc: buildPageTemplate(template.id) }))
const filteredTemplates = computed(() => {
  const query = String(searchQuery.value || '').trim().toLowerCase()
  if (!query) return templatesWithDocs
  return templatesWithDocs.filter(template => [template.name, template.category, template.description, ...(template.tags || [])].some(value => String(value || '').toLowerCase().includes(query)))
})
const editorViewportLabel = computed(() => editorViewportOptions.find(option => option.key === editorViewportMode.value)?.label || 'Desktop')
const previewViewportClasses = computed(() => {
  if (editorViewportMode.value === 'mobile') return 'w-[390px] max-w-full rounded-[28px] ring-8 ring-zinc-800'
  if (editorViewportMode.value === 'tablet') return 'w-[820px] max-w-full rounded-xl ring-1 ring-zinc-300'
  return 'w-full max-w-[1320px] ring-1 ring-zinc-200'
})

const setEditorViewportMode = (mode) => {
  if (!editorViewportOptions.some(option => option.key === mode)) return
  editorViewportMode.value = mode
  try { localStorage.setItem(EDITOR_VIEWPORT_STORAGE_KEY, mode) } catch (error) { console.debug('[PageThemeEditor] Failed to persist viewport mode', error) }
}
const openPreview = (template) => { previewTemplate.value = template || null }
const closePreview = () => { if (!applying.value) previewTemplate.value = null }

const selectTemplate = async (template) => {
  if (!template || applying.value) return
  applying.value = true
  applyingTemplateId.value = template.id
  let pageId = route.query.pageId || route.params.pageId
  if (!pageId && process.env.NODE_ENV === 'development') pageId = '9999'
  if (!pageId) {
    window.alert('No page ID found for this editor.')
    applying.value = false
    applyingTemplateId.value = ''
    return
  }

  const initialJSON = buildPageTemplate(template.id)
  let canonicalTitle = null
  let existingSeoTitle = null
  try {
    const apiProxy = process.env.VUE_APP_API_PROXY_URL
    const [metaRes, contentRes] = await Promise.all([
      fetch(`${apiProxy}/api/pages/${pageId}/meta`, { credentials: 'include' }),
      fetch(`${apiProxy}/api/pages/${pageId}/content`, { credentials: 'include' }),
    ])
    if (metaRes.ok) {
      const meta = await metaRes.json()
      canonicalTitle = meta?.title || meta?.page?.title || null
    }
    if (contentRes.ok) {
      const content = await contentRes.json()
      const doc = content?.draft || content?.published
      if (doc?.seo?.title) existingSeoTitle = String(doc.seo.title)
    }
  } catch (error) {
    console.warn('[apply-template] Meta/content fetch failed, using template defaults', error)
  }

  const finalTitle = String(canonicalTitle || existingSeoTitle || initialJSON.title || 'Untitled Page')
  initialJSON.title = finalTitle
  initialJSON.seo = { ...initialJSON.seo, title: String(existingSeoTitle || finalTitle) }
  const writeSessionFallback = () => {
    try { sessionStorage.setItem(`pageTemplate:${pageId}`, JSON.stringify(initialJSON)) } catch (error) { console.warn('[apply-template:fallback] sessionStorage failed', error) }
  }

  try {
    const apiProxy = process.env.VUE_APP_API_PROXY_URL
    const response = await fetch(`${apiProxy}/api/pages/${pageId}/apply-template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title: initialJSON.title, templateSections: initialJSON.sections, templateKey: template.id, templateVersion: '2' }),
    })
    if (!response.ok) {
      console.warn('[apply-template] Backend rejected template:', response.status, await response.text().catch(() => ''))
      writeSessionFallback()
    }
  } catch (error) {
    console.warn('[apply-template] Network error', error)
    writeSessionFallback()
  } finally {
    await router.push({ name: 'SitePageEditor', params: { pageId: String(pageId) } })
    applying.value = false
    applyingTemplateId.value = ''
  }
}

onMounted(() => {
  try {
    const storedViewportMode = localStorage.getItem(EDITOR_VIEWPORT_STORAGE_KEY)
    if (storedViewportMode) setEditorViewportMode(storedViewportMode)
  } catch (error) {
    console.debug('[PageThemeEditor] Failed to load viewport mode', error)
  }
})
</script>

<style scoped>
.template-card-canvas {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1200px;
  min-height: 760px;
  transform: translateX(-50%) scale(0.36);
  transform-origin: top center;
}
.theme-tooltip { position: relative; }
.theme-tooltip::after {
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  z-index: 100;
  transform: translateX(-50%) translateY(-2px);
  border-radius: 7px;
  background: rgba(24, 24, 27, 0.94);
  color: white;
  content: attr(data-tooltip);
  font-size: 11px;
  line-height: 1;
  opacity: 0;
  padding: 7px 9px;
  pointer-events: none;
  transition: opacity 150ms ease, transform 150ms ease;
  white-space: nowrap;
}
.theme-tooltip:hover::after, .theme-tooltip:focus-visible::after { opacity: 1; transform: translateX(-50%) translateY(0); }
.theme-library-scroll { scrollbar-color: rgba(113, 113, 122, 0.45) transparent; scrollbar-width: thin; }
.theme-library-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
.theme-library-scroll::-webkit-scrollbar-track { background: transparent; }
.theme-library-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(113, 113, 122, 0.4); }
.theme-library-scroll::-webkit-scrollbar-thumb:hover { background: rgba(82, 82, 91, 0.6); }
</style>
